<script setup lang="ts">
import {
  EvaluationParameterDto,
  EvaluationResultDto,
  instanceOfEnumerationEvaluationParameterDto,
  instanceOfEnumerationEvaluationResultDto,
  instanceOfNumericEvaluationParameterDto,
  instanceOfNumericEvaluationResultDto,
} from '@/api/generator';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils';
import { getFormattingOptionsFromEvaluationParameter, toDisplayValue } from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import JobExecutionTrendViewDialog from '@/views/jobOccurrences/components/JobExecutionTrendViewDialog.vue';
import { getSetupParameterName } from '@/views/tasks/utils.ts';
import { useTranslation } from 'i18next-vue';
import { computed } from 'vue';
import LinearGauge from '../charts/LinearGauge.vue';
import { getLimitedValue } from '../charts/utils/limitedValue';
import CommentInlineDialog from '../comments/CommentInlineDialog.vue';
import EvaluationState from '../evaluation/EvaluationState.vue';

const { t } = useTranslation();

type AnalysisResultOptions = {
  enableComments?: boolean;
  enableTrends?: boolean;
  enableEditing?: boolean;
  readonlyComments?: boolean;
  // Defaults to true (standing "add a comment" affordance); set false to hide the comment icon
  // entirely on rows that have no comments yet.
  commentCountAlways?: boolean;
};

const props = withDefaults(
  defineProps<{
    value?: EvaluationResultDto[];
    evaluations?: EvaluationParameterDto[];
    options?: AnalysisResultOptions;
    disabled?: boolean;
    // Per-row override for the limit gauge & result columns, keyed by evaluationParameterId �
    // e.g. TrackIt only grays out the ONE row currently being edited there, unlike `disabled`
    // (state === Ignored) which applies uniformly to every row of the result. Falls back to
    // `disabled` when not given, so other consumers are unaffected.
    rowDisabled?: (evaluationParameterId: string) => boolean;
    // Per-row override for the value column, keyed by evaluationParameterId � e.g. TrackIt keeps
    // the row actually being edited enabled while every other row is locked out, the inverse
    // scope of `rowDisabled`. Falls back to `disabled` when not given.
    valueDisabled?: (evaluationParameterId: string) => boolean;
  }>(),
  {
    value: undefined,
    evaluations: undefined,
    disabled: false,
    rowDisabled: undefined,
    valueDisabled: undefined,
    options: () => ({
      enableComments: true,
      enableTrends: true,
    }),
  },
);

const emits = defineEmits<{
  change: [];
}>();

function getResult(evaluation: EvaluationParameterDto) {
  return props.value?.find((r) => r.evaluationParameterId === evaluation.id);
}

function isRowDisabled(evaluationParameterId: string) {
  return props.rowDisabled ? props.rowDisabled(evaluationParameterId) : props.disabled;
}

function isValueDisabled(evaluationParameterId: string) {
  return props.valueDisabled ? props.valueDisabled(evaluationParameterId) : props.disabled;
}

const items = computed(() => {
  return props.evaluations?.map((evaluation) => {
    const result = getResult(evaluation);
    const numericResult =
      result && instanceOfNumericEvaluationResultDto(result) ? result : undefined;
    const enumerationResult =
      result && instanceOfEnumerationEvaluationResultDto(result) ? result : undefined;

    return {
      title: getSetupParameterName(evaluation),
      limitedValue:
        numericResult && instanceOfNumericEvaluationParameterDto(evaluation)
          ? getLimitedValue(numericResult, evaluation)
          : undefined,

      deviation: {
        value: numericResult?.deviationValue,
        unitAbbreviation: numericResult?.deviationUnit,
        decimalPlaces: numericResult?.decimalPlaces,
        valueType: getFormattingOptionsFromEvaluationParameter(evaluation)?.valueType,
      },

      selectedOptionName: enumerationResult?.selectedOption?.name,

      // Type-agnostic "does this row already have a real value" � a numeric deviation or a
      // picked enumeration option � for callers that need to know without caring which.
      hasValue: instanceOfEnumerationEvaluationParameterDto(evaluation)
        ? !!enumerationResult?.selectedOption
        : numericResult?.deviationValue != null,

      evaluationInfo: evaluation,

      outcome: result?.evaluationOutcome,

      trend: result?.id,

      result,
    };
  });
});

const headers = computed(() => [
  {
    title: t('Quantity_one'),
    key: 'title',
  },
  {
    title: t('EvaluationType_one'),
    key: 'evaluationInfo',
  },
  {
    title: t('Value'),
    key: 'deviation',
  },
  {
    title: t('Limit_other'),
    key: 'limitedValue',
  },
  {
    title: t('Result_one'),
    key: 'outcome',
  },
  ...(props.options.enableTrends ? [{ key: 'trend', sortable: false }] : []),
  ...(props.options.enableComments ? [{ key: 'comments', sortable: false }] : []),
  ...(props.options.enableEditing ? [{ key: 'editing', sortable: false }] : []),
]);
</script>

<template>
  <v-data-table v-if="items" :headers="headers" :items="items" itemsPerPage="-1" hideDefaultFooter>
    <template #item.title="{ value: title }">
      <v-label class="text-justify">{{ title }}</v-label>
    </template>

    <template #item.evaluationInfo="{ value: { evaluationType } }">
      <div class="text-justify">
        {{
          evaluationType ? $t(`EvaluationTypeEnum.${evaluationType}` as I18nKey) : EMPTY_FIELD_SIGN
        }}
      </div>
    </template>

    <template #item.limitedValue="{ item, value: limitedValue }">
      <LinearGauge
        v-if="limitedValue"
        :value="limitedValue"
        :unit="item.deviation.unitAbbreviation ?? undefined"
        :disabled="isRowDisabled(item.evaluationInfo.id)"
      />
    </template>

    <template #item.deviation="{ item, value: deviation }">
      <slot
        name="value"
        :item="item"
        :deviation="deviation"
        :disabled="isValueDisabled(item.evaluationInfo.id)"
      >
        <template v-if="instanceOfEnumerationEvaluationParameterDto(item.evaluationInfo)">
          {{ item.selectedOptionName ?? EMPTY_FIELD_SIGN }}
        </template>
        <template v-else>
          {{
            toDisplayValue(deviation.value, {
              fractionDigits: deviation.decimalPlaces,
              valueType: deviation.valueType,
              unitAbbreviation: deviation.unitAbbreviation,
            })
          }}
        </template>
      </slot>
    </template>

    <template #item.outcome="{ item, value: outcome }">
      <EvaluationState
        :value="outcome"
        :disabled="isRowDisabled(item.evaluationInfo.id)"
        class="ml-2"
      />
    </template>

    <template #item.trend="{ value: evaluationResultId }">
      <JobExecutionTrendViewDialog :evaluationResultId="evaluationResultId" :disabled="disabled" />
    </template>

    <template #item.comments="{ item: { result } }">
      <CommentInlineDialog
        v-if="result"
        :entity="result"
        entityWithCommentType="EvaluationResultOrDataPoint"
        :disabled="disabled"
        :readonly="options.readonlyComments"
        :showCountAlways="options.commentCountAlways ?? true"
        @change="emits('change')"
      />
    </template>

    <template #item.editing="{ item }">
      <slot name="editing" :item="item" :disabled="disabled" />
    </template>
  </v-data-table>
</template>

<style scoped lang="scss">
:deep(.v-data-table__td) {
  vertical-align: top;
  padding-left: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  min-height: unset !important;
  height: unset !important;
}
</style>
